'use client';
import { imageSource, resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { VariantsEntity } from '@eliadmin/core/src/api/products/types/product-types';
import type { ProductDetailsWithSlug } from '@eliadmin/core/src/stores/variant-store';

import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { lazy as lazyProductLightbox, Suspense as ProductLightboxBoundary } from 'react';

const LightboxComponent = lazyProductLightbox(() =>
  import('@eliadmin/core/src/components/lightbox/lightbox-component').then(module => ({
    default: module.LightboxComponent,
  })),
);
import { useState, useRef, useEffect } from 'react';

export function ProductImages({ inDeal = false }: { inDeal?: boolean }) {
  const templateSettings = useGlobalStore(state => state.templateSettings);
  const product = useVariantsStore(s => s.product);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);

  const options = {
    loop: true,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex, api } = carouselContext;

  const images = filerImages(
    templateSettings.product_detail?.gallery_with_image_variants?.enabled ?? false,
    product,
    selectedVariant,
  );

  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const [pendingThumbnail, setPendingThumbnail] = useState<number | null>(null);
  useEffect(() => {
    if (!api || pendingThumbnail === null) return;
    api.scrollTo(pendingThumbnail);
    setPendingThumbnail(null);
  }, [api, pendingThumbnail]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth >= 768) return;

    const container = thumbnailContainerRef.current;
    if (!container) return;

    const gap = 6;
    const thumbWidth = (container.clientWidth - 4 * gap) / 5;
    const scrollLeft =
      selectedIndex * (thumbWidth + gap) - container.clientWidth / 2 + thumbWidth / 2;
    container.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
  }, [selectedIndex]);

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  return (
    <>
      {isOpen && (
        <ProductLightboxBoundary fallback={null}>
          <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
        </ProductLightboxBoundary>
      )}
      <div
        className={cn(
          'flex w-full max-w-[688px] flex-col items-start justify-start gap-3 sm:flex-row sm:gap-4',
          inDeal ? 'sm:flex-col-reverse' : 'sm:flex-row',
        )}
      >
        <div
          className={cn(
            'hidden w-full max-w-[96px] items-start justify-start gap-4 sm:flex',
            inDeal ? 'flex-row' : 'flex-col',
          )}
        >
          {images.map((image, index) => (
            <button
              type="button"
              key={`${image?.url}-${index}`}
              aria-label={`Zobraziť obrázok ${index + 1}`}
              aria-pressed={index === selectedIndex}
              onClick={() => setPendingThumbnail(index)}
              className="shrink-0 rounded-[20px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
            >
              <Image
                src={image?.url ?? '/images/placeholder.png'}
                key={image?.url}
                alt={resolveImageAlt(image, product?.name)}
                width={96}
                height={96}
                sizes="96px"
                className={cn(
                  'max-h-24 min-h-24 min-w-24 max-w-24 cursor-pointer rounded-[20px] object-contain object-center p-2',
                  index === selectedIndex ? 'border-2 border-primary' : 'border border-border',
                )}
              />
            </button>
          ))}
        </div>
        <div
          className={cn(
            'w-full rounded-[20px] border',
            inDeal ? 'md:max-w-[569px]' : 'max-h-[630px] md:max-w-[576px]',
          )}
        >
          <Carousel
            options={options}
            className={{ wrapper: 'w-full', main: 'w-full', slide: 'w-full', container: 'w-full' }}
            slides={images.map((image, index) => (
              <button
                type="button"
                aria-label={`Otvoriť galériu produktu, obrázok ${index + 1}`}
                key={index}
                className="relative aspect-square w-full"
                onClick={handleOpenGallery(images, index)}
              >
                <Image
                  src={image?.url ?? '/images/placeholder.png'}
                  alt={resolveImageAlt(image, product?.name)}
                  fill
                  priority={!inDeal && index === 0}
                  fetchPriority={!inDeal && index === 0 ? 'high' : 'low'}
                  sizes="(min-width: 768px) 576px, calc(100vw - 32px)"
                  className="absolute inset-0 z-0 h-full w-full object-contain object-center p-4"
                />
              </button>
            ))}
          />
        </div>
        <div className="flex w-full items-center justify-center gap-1 sm:hidden">
          {images.map((_, index) => (
            <div
              key={index}
              className={cn(
                'h-[7px] w-[7px] rounded-full',
                index === selectedIndex ? 'bg-foreground' : 'bg-[#E5E7EB]',
              )}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function filerImages(
  showOnlyVariantImages: boolean,
  product: ProductDetailsWithSlug,
  selectedVariant?: VariantsEntity,
) {
  const variantImages = Array.isArray(selectedVariant?.images) ? selectedVariant.images : [];
  const productImages = Array.isArray(product?.images) ? product.images : [];
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const images =
    showOnlyVariantImages && variantImages.some(image => imageSource(image))
      ? variantImages
      : [
          product?.image,
          ...productImages,
          ...variants.flatMap(variant => (Array.isArray(variant?.images) ? variant.images : [])),
        ];
  return images.filter((image): image is NonNullable<typeof image> => Boolean(imageSource(image)));
}
